/**
 * Tax on a money entry (L43 and step 10.5 of docs/plans/ledger.md): which
 * tax a line carries, where it goes, and the context the server hands the
 * patterns so they can work it out.
 *
 * The arithmetic is never here. `computeDocument` of `document-math.ts` is
 * the only tax arithmetic: the patterns call it line by line, the same call a
 * tax invoice makes, so what an entry counts and what its invoice prints agree to the paisa.
 * This file only decides the split (`taxSplitOf`) and names the accounts.
 *
 * The place of supply (India): an override on the entry, else the party's
 * GSTIN (its first two digits are the state), else the party's State field,
 * else the business's own state. Inside the business's state the tax is CGST
 * and SGST (or UTGST in a union territory without a legislature); between
 * states it is IGST.
 */
import type { DocTaxSplit } from '../document-math';
import { gstStateCodeOf, gstStateOf } from '../gst-states';
import { parseGstin } from '../gstin';
import type { TaxRegime } from '../tax-countries';

/** What one party row says about where it is, read by the server from its Tax ID and State. */
export type PartyTaxFacts = {
  /** Its Tax ID as typed: a GSTIN, a TRN or a VAT number. */
  readonly taxId: string | null;
  /** Its GST state code: from the GSTIN's first two digits, else its State field. */
  readonly state: string | null;
};

/** The workspace's tax, as `buildLines` reads it. With none, no line carries tax. */
export type PostingTax = {
  readonly regime: TaxRegime;
  /** The business's GST state code (in_gst): its State detail, else its GSTIN's first two digits. */
  readonly businessState: string | null;
  /** Each party's facts, by lower-case party id. */
  readonly parties?: Readonly<Record<string, PartyTaxFacts>>;
};

/** A GST state code from a GSTIN or what a person typed in a State field ("27", "Maharashtra"); null when it names none. */
export const stateCodeFrom = (input: { readonly taxId?: string | null; readonly state?: string | null }): string | null => {
  const fromId = typeof input.taxId === 'string' && input.taxId.trim() !== '' ? (parseGstin(input.taxId)?.state ?? null) : null;
  if (fromId !== null) return fromId;
  return typeof input.state === 'string' && input.state.trim() !== '' ? gstStateCodeOf(input.state) : null;
};

/** A party list's field that holds its Tax ID, by its label: GSTIN, Tax ID, TRN, a VAT number. */
export const TAX_ID_LABEL = /\b(gstin|gst\s*(no|number|in)|tax\s*id|trn|vat(\s*(no|number|id))?|tin)\b/i;
/** A party list's field that holds its State, by its label. */
export const STATE_LABEL = /^\s*(state|state\s*code|place\s*of\s*supply)\s*$/i;

/** The facts of one party from its Tax ID and State as typed. */
export const partyTaxFacts = (taxId: string | null, state: string | null): PartyTaxFacts => ({
  taxId: taxId !== null && taxId.trim() !== '' ? taxId.trim() : null,
  state: stateCodeFrom({ taxId, state }),
});

/**
 * How the tax divides for a regime and a place of supply. `none` posts no
 * tax; `single` and `sales_tax` are one tax at the line's rate; `in_gst` is
 * CGST and SGST (UTGST) inside the business's state and IGST between states.
 * A place or a business state that is not known is read as inside the state,
 * as a printed invoice reads it.
 */
export const taxSplitOf = (input: { readonly regime: TaxRegime | null; readonly businessState: string | null; readonly place: string | null }): DocTaxSplit => {
  if (input.regime === null || input.regime === 'none') return 'none';
  if (input.regime === 'single' || input.regime === 'sales_tax') return 'single';
  const home = input.businessState === null ? null : gstStateOf(input.businessState);
  const place = input.place === null ? null : gstStateOf(input.place);
  if (home !== null && place !== null && home.code !== place.code) return 'inter';
  return home?.ut === true ? 'intra_ut' : 'intra';
};

/** The business's state from its details: the State detail, else its GSTIN. */
export const businessStateOf = (details: { readonly state?: unknown; readonly gstin?: unknown }): string | null =>
  stateCodeFrom({
    taxId: typeof details.gstin === 'string' ? details.gstin : null,
    state: typeof details.state === 'string' ? details.state : null,
  });

/** Which side of the tax a pattern's lines are on: tax collected on a sale, tax you can claim on a purchase. */
export type TaxSide = 'output' | 'input';

/** One head of tax, as `document-math.ts` names its figures. */
export type TaxHead = 'cgst' | 'sgst' | 'utgst' | 'igst' | 'single';
export const TAX_HEADS: readonly TaxHead[] = ['cgst', 'sgst', 'utgst', 'igst', 'single'];

/** The account system key a head of tax posts to (the money kit seeds them for the regime, 10.6). */
export const taxAccountKey = (side: TaxSide, head: TaxHead): string => {
  const prefix = side === 'output' ? 'tax_out' : 'tax_in';
  return head === 'single' ? prefix : `${prefix}_${head}`;
};

/** Every tax account system key: what the tax card and the tax summary read. */
export const TAX_ACCOUNT_KEYS: readonly string[] = (['output', 'input'] as const).flatMap((side) => TAX_HEADS.map((head) => taxAccountKey(side, head)));

/** The side and head an account system key names, or null when it is not a tax account. */
export const taxAccountOf = (systemKey: string | null): { readonly side: TaxSide; readonly head: TaxHead } | null => {
  if (systemKey === null) return null;
  for (const side of ['output', 'input'] as const) for (const head of TAX_HEADS) if (taxAccountKey(side, head) === systemKey) return { side, head };
  return null;
};

/**
 * What a tax line keeps in its `dimensions.tax`, so the tax summary can be
 * read from the lines alone: the rate, the HSN or SAC code, the head, the
 * taxable value it was worked on (on the first head of a line only, so the
 * taxable is never counted twice), and the buyer's or supplier's Tax ID.
 */
export type TaxLineFacts = {
  readonly rate_bp: number;
  readonly hsn: string | null;
  readonly head: TaxHead;
  /** Exact decimal; present on the first head of each group (CGST, IGST or the single tax). */
  readonly taxable?: string;
  readonly party_tax_id: string | null;
  readonly place: string | null;
};

/** The heads whose line carries the taxable value: one per group, never the SGST or UTGST half. */
export const carriesTaxable = (head: TaxHead): boolean => head === 'cgst' || head === 'igst' || head === 'single';
